import { useEffect, useRef, useState } from 'react';
import { useFirebase } from '@/firebase';

import { BUFFER_SIZE } from '../canvas/types';

import { MAP_SYMBOLS } from '@/lib/map-assets';
import { Location } from '@/lib/types';

export function useMapAssets(location: Location | null) {
    const { backend } = useFirebase();
    const landRef = useRef<HTMLCanvasElement | null>(null);
    const colorRef = useRef<HTMLCanvasElement | null>(null);
    const symbolsRef = useRef<HTMLCanvasElement | null>(null);
    const assemblerRef = useRef<HTMLCanvasElement | null>(null);
    const landMaskRef = useRef<Uint8Array | null>(null);
    const stampMaskRef = useRef<Uint8Array | null>(null);
    const patternsRef = useRef<{ land: CanvasPattern | null, water: CanvasPattern | null }>({ land: null, water: null });
    const customMapImageRef = useRef<HTMLImageElement | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(0);
    const [textures, setTextures] = useState<{ land: HTMLImageElement | null, water: HTMLImageElement | null }>({ land: null, water: null });
    const [processedSheets, setProcessedSheets] = useState<Record<string, HTMLCanvasElement | HTMLImageElement>>({});

    useEffect(() => {
        const create = () => {
            const c = document.createElement('canvas');
            c.width = BUFFER_SIZE;
            c.height = BUFFER_SIZE;
            const ctx = c.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
            return c;
        };

        const loadImg = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = () => {
                console.warn(`Failed to load image with CORS: ${url}. Retrying without CORS...`);
                const fallbackImg = new Image();
                fallbackImg.onload = () => resolve(fallbackImg);
                fallbackImg.onerror = () => reject(new Error(`Failed to load: ${url}`));
                fallbackImg.src = url;
            };
            img.src = url;
        });

        const processIndividualStamp = (img: HTMLImageElement) => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                if (data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245) {
                    data[i + 3] = 0;
                }
            }

            ctx.putImageData(imageData, 0, 0);
            return canvas;
        };

        const init = async () => {
            setIsLoading(true);
            if (!landRef.current) landRef.current = create();
            if (!colorRef.current) colorRef.current = create();
            if (!symbolsRef.current) symbolsRef.current = create();
            if (!assemblerRef.current) assemblerRef.current = create();
            if (!landMaskRef.current) landMaskRef.current = new Uint8Array(BUFFER_SIZE * BUFFER_SIZE);
            if (!stampMaskRef.current) stampMaskRef.current = new Uint8Array(BUFFER_SIZE * BUFFER_SIZE);

            try {
                const [lTex, wTex] = await Promise.all([
                    loadImg('/textures/land.png'),
                    loadImg('/textures/water.png')
                ]);
                setTextures({ land: lTex, water: wTex });

                const dummyCtx = document.createElement('canvas').getContext('2d')!;
                patternsRef.current = {
                    land: dummyCtx.createPattern(lTex, 'repeat'),
                    water: dummyCtx.createPattern(wTex, 'repeat')
                };

                const individualAssets: Record<string, HTMLCanvasElement> = {};
                const uniqueSrcs = Array.from(new Set(MAP_SYMBOLS.map(s => s.src).filter(Boolean))) as string[];

                const stampResults = await Promise.allSettled(
                    uniqueSrcs.map(async src => {
                        const img = await loadImg(src);
                        return { src, canvas: processIndividualStamp(img) };
                    })
                );

                stampResults.forEach(result => {
                    if (result.status === 'fulfilled') {
                        individualAssets[result.value.src] = result.value.canvas;
                    }
                });

                setProcessedSheets({ ...individualAssets });

            } catch (e) {
                console.error("Failed to load map assets:", e);
            }

            const resolveUrl = async (url?: string | null, path?: string | null) => {
                if (!url) return undefined;
                if (!backend) return url;
                if (path) {
                    try {
                        return await backend.assets.resolveSecureAssetUrl(path);
                    } catch(e) {}
                }
                if (url) {
                    try {
                        return await backend.assets.resolveSecureAssetUrl(url);
                    } catch(e) {}
                }
                return url;
            };

            const landUrl = await resolveUrl(location?.mapLandUrl, location?.mapLandStoragePath);
            const colorUrl = await resolveUrl(location?.mapColorUrl, location?.mapColorStoragePath);
            const symbolsUrl = await resolveUrl(location?.mapSymbolsUrl, location?.mapSymbolsStoragePath);
            const originalMapUrl = await resolveUrl(location?.originalMapUrl, location?.originalMapStoragePath);
            if (originalMapUrl) {
                try {
                    const [cMapImg, cImg] = await Promise.all([
                        loadImg(originalMapUrl).catch(() => null),
                        colorUrl ? loadImg(colorUrl).catch(() => null) : null
                    ]);

                    if (cMapImg) {
                        customMapImageRef.current = cMapImg;
                        const w = cMapImg.naturalWidth;
                        const h = cMapImg.naturalHeight;
                        if (landRef.current) { landRef.current.width = w; landRef.current.height = h; }
                        if (colorRef.current) { colorRef.current.width = w; colorRef.current.height = h; }
                        if (symbolsRef.current) { symbolsRef.current.width = w; symbolsRef.current.height = h; }
                        if (assemblerRef.current) { assemblerRef.current.width = w; assemblerRef.current.height = h; }
                    } else {
                        customMapImageRef.current = null;
                    }

                    const w = cMapImg ? cMapImg.naturalWidth : BUFFER_SIZE;
                    const h = cMapImg ? cMapImg.naturalHeight : BUFFER_SIZE;

                    if (cImg && colorRef.current) {
                        const ctx = colorRef.current.getContext('2d')!;
                        ctx.clearRect(0, 0, w, h);
                        ctx.drawImage(cImg, 0, 0, w, h);
                    } else if (colorRef.current) {
                        const ctx = colorRef.current.getContext('2d')!;
                        ctx.clearRect(0, 0, w, h);
                    }

                    if (landRef.current) {
                        const ctx = landRef.current.getContext('2d')!;
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, w, h);
                    }

                    if (landMaskRef.current) {
                        landMaskRef.current.fill(255);
                    }
                } catch (e) {
                    console.error("Failed to load custom uploaded map layers:", e);
                }
            } else if (landUrl || colorUrl || symbolsUrl) {
                customMapImageRef.current = null;
                try {
                    const [lImg, cImg, sImg] = await Promise.all([
                        landUrl ? loadImg(landUrl).catch(() => null) : null,
                        colorUrl ? loadImg(colorUrl).catch(() => null) : null,
                        symbolsUrl ? loadImg(symbolsUrl).catch(() => null) : null
                    ]);

                    if (lImg && landRef.current) {
                        const ctx = landRef.current.getContext('2d')!;
                        ctx.clearRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        ctx.drawImage(lImg, 0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        const imgData = ctx.getImageData(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        if (landMaskRef.current) {
                            for (let i = 0; i < imgData.data.length; i += 4) {
                                landMaskRef.current[i / 4] = imgData.data[i + 3] > 128 ? 255 : 0;
                            }
                        }
                    }
                    if (cImg && colorRef.current) {
                        const ctx = colorRef.current.getContext('2d')!;
                        ctx.clearRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        ctx.drawImage(cImg, 0, 0, BUFFER_SIZE, BUFFER_SIZE);
                    }
                    if (sImg && symbolsRef.current) {
                        const ctx = symbolsRef.current.getContext('2d')!;
                        ctx.clearRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        ctx.drawImage(sImg, 0, 0, BUFFER_SIZE, BUFFER_SIZE);
                    }
                } catch (e) {
                    console.error("Failed to load map layers:", e);
                }
            } else {
                customMapImageRef.current = null;
            }
            setIsLoading(false);
            setLastUpdated(Date.now());
        };

        const timer = setTimeout(init, 50);
        return () => clearTimeout(timer);
    }, [location?.mapLandUrl, location?.mapColorUrl, location?.mapSymbolsUrl, location?.originalMapUrl]);

    return {
        landRef, colorRef, symbolsRef, assemblerRef, patternsRef, landMaskRef, stampMaskRef,
        customMapImageRef,
        isLoading, lastUpdated, textures, processedSheets
    };
}
